/**
 * Explicit run scenarios for the fixture provider and for tests
 * (AI-CONNECT-001.2).
 *
 * These are TEST SCENARIOS, not a product rule and not a pricing basis: every
 * amount, token counter, execution and settlement below is written out, never
 * derived (settled = 8 micro-credits consumed against a 16 estimate). The UI
 * must show whatever the provider returns and must not infer a charge, a
 * release, a token price or a "remaining tokens" figure from them.
 *
 * The four scenarios mirror the handoff:
 *   settled  - provider ran, usage known, hold consumed and the rest released;
 *   pending  - provider call started, result without billable usage
 *              (`reconciliation` + `usage_unknown`): the hold STAYS, consumed
 *              and released are `null`, the charge is `null`;
 *   failed   - released before the provider was attempted: charge 0, the
 *              whole hold released;
 *   no-data  - no stock: no run row, nothing reserved, charge 0.
 */
import {
  AI_NO_DATA_MESSAGE,
  AI_TOOL_INVENTORY_STOCK,
  type AiEvidence,
  type AiExecution,
  type AiMoney,
  type AiRunState,
  type AiSettlement,
  type AiTurn,
  type AiUsageTokens,
} from './contract.ts';

export type FixtureScenario = 'settled' | 'pending' | 'failed' | 'no-data';

const money = (microCredits: string, toman: string): AiMoney => ({
  microCredits,
  toman,
  suppressedBelowOneToman: false,
});

const ZERO = money('0', '0');
const HOLD = money('16', '4');
const CONSUMED = money('8', '2');
const RELEASED_REST = money('8', '2');

const NO_TOKENS: AiUsageTokens = {
  inputTokens: null,
  cachedInputTokens: null,
  outputTokens: null,
  reasoningTokens: null,
};

export const FIXTURE_STOCK: readonly AiEvidence[] = [
  {
    entity: 'warehouse',
    recordId: 'WH-1',
    onHand: '3/1',
    unit: 'quantity',
    extractedAt: '2026-10-14T00:00:00.000Z',
    source: 'inventory.stock.summary',
  },
];

/** Backend `USAGE_STATE_LABEL`. */
export const STATE_LABELS: Readonly<Record<AiRunState, string>> = {
  pending: 'در انتظار',
  failed: 'ناموفق',
  refunded: 'برگشت‌شده',
  settled: 'تسویه‌شده',
};

export const TOMAN_UNAVAILABLE_LABEL = 'مبلغ تومان هنوز تعیین نشده';

export interface FixtureScenarioSpec {
  readonly state: AiRunState;
  readonly hasRun: boolean;
  readonly answer: string | null;
  readonly evidence: readonly AiEvidence[];
  readonly usage: AiUsageTokens;
  readonly estimate: AiMoney;
  /** Customer charge; `null` while settlement is open. */
  readonly charge: AiMoney | null;
  readonly amountToman: string | null;
  readonly amountLabel: string;
  readonly execution: AiExecution;
  readonly settlement: AiSettlement;
}

export const FIXTURE_SCENARIOS: Readonly<Record<FixtureScenario, FixtureScenarioSpec>> = {
  settled: {
    state: 'settled',
    hasRun: true,
    answer: 'موجودی ثبت‌شده است',
    evidence: FIXTURE_STOCK,
    usage: {
      inputTokens: '120',
      cachedInputTokens: '40',
      outputTokens: '30',
      reasoningTokens: '0',
    },
    estimate: HOLD,
    charge: CONSUMED,
    amountToman: '2',
    amountLabel: '2 تومان',
    execution: { status: 'completed', providerAttempted: true },
    settlement: {
      status: 'settled',
      usagePending: false,
      held: ZERO,
      consumed: CONSUMED,
      released: RELEASED_REST,
    },
  },
  pending: {
    state: 'pending',
    hasRun: true,
    answer: null,
    evidence: [],
    usage: NO_TOKENS,
    estimate: HOLD,
    charge: null,
    amountToman: null,
    amountLabel: TOMAN_UNAVAILABLE_LABEL,
    execution: { status: 'reconciliation', providerAttempted: true },
    settlement: {
      status: 'usage_unknown',
      usagePending: true,
      held: HOLD,
      consumed: null,
      released: null,
    },
  },
  failed: {
    state: 'failed',
    hasRun: true,
    answer: null,
    evidence: [],
    usage: NO_TOKENS,
    estimate: HOLD,
    charge: ZERO,
    amountToman: '0',
    amountLabel: '0 تومان',
    execution: { status: 'failed', providerAttempted: false },
    settlement: {
      status: 'released',
      usagePending: false,
      held: ZERO,
      consumed: ZERO,
      released: HOLD,
    },
  },
  'no-data': {
    state: 'settled',
    hasRun: false,
    answer: AI_NO_DATA_MESSAGE,
    evidence: [],
    usage: NO_TOKENS,
    estimate: ZERO,
    charge: ZERO,
    amountToman: '0',
    amountLabel: '0 تومان',
    execution: { status: 'not_started', providerAttempted: false },
    settlement: {
      status: 'settled',
      usagePending: false,
      held: ZERO,
      consumed: ZERO,
      released: ZERO,
    },
  },
};

export interface FixtureIds {
  readonly turnId: string;
  readonly runId: string;
  readonly quoteId: string;
  readonly correlationId: string;
}

export function turnFromScenario(scenario: FixtureScenario, ids: FixtureIds): AiTurn {
  const spec = FIXTURE_SCENARIOS[scenario];
  return {
    turnId: ids.turnId,
    runId: spec.hasRun ? ids.runId : null,
    quoteId: ids.quoteId,
    correlationId: spec.hasRun ? ids.correlationId : null,
    state: spec.state,
    stateLabel: STATE_LABELS[spec.state],
    domain: 'inventory',
    tool: AI_TOOL_INVENTORY_STOCK,
    answer: spec.answer,
    evidence: spec.evidence,
    usage: spec.usage,
    estimate: spec.estimate,
    charge: spec.charge,
    amountToman: spec.amountToman,
    amountLabel: spec.amountLabel,
    replayed: false,
    budgetWarnings: [],
    importMappingFree: true,
    importMappingCustomerChargeToman: '0',
  };
}

/**
 * Late settlement of a pending run (`settleLateUsage`): the usage becomes
 * known, the hold is consumed and the rest released. Explicit figures again.
 */
export const LATE_SETTLEMENT = {
  usage: { inputTokens: '90', cachedInputTokens: '0', outputTokens: '20', reasoningTokens: '0' },
  charge: CONSUMED,
  amountToman: '2',
  amountLabel: '2 تومان',
  settlement: {
    status: 'settled',
    usagePending: false,
    held: ZERO,
    consumed: CONSUMED,
    released: RELEASED_REST,
  },
  execution: { status: 'completed', providerAttempted: true },
  answer: 'موجودی ثبت‌شده است',
  evidence: FIXTURE_STOCK,
} as const;
